import { GLResponse, GLRequest, GLPagingRequest } from "gl-commonui";

export interface NotificationModel {
    id?: string,
    title?: string,
    description?: string,
    startDate?: string,
    endDate?: string,
    notificationType?: number,
    sticky?: boolean,
    channel?: number,
    createdBy?: string,
    creationDate?: string,
    status?: NotificationStatus,
    roleTargets?: NotificationRoleTargetModel[],
    isAdvanced?: boolean,
    advancedDescription?: string
}

export interface NotificationRoleTargetModel {
    role: NotificationRoles,
    targetType: NotificationTargetTypes,
    targetId: string,
    targetName: string,
    isCalculated?: boolean,
    tenantId?: number | null,
    regionId?: string,
    schoolId?: string,
    campusId?: string,
    classId?: string
}

// NotificationService.getRegions() response shape — a real region (id/name) plus the
// tenant it routes to, using the same null/1/2/3 (Global/US/China/Russia) convention as
// `/admin/v1/notifications/targetcount`.
export interface NotificationRegionModel {
    id: string,
    name?: string,
    tenantId: number | null,
}
export interface NotificationTargetCount {
    roleTargets: NotificationRoleTargetModel[],
    notifiedUserCount: number
}
export class GetNotificationRequest extends GLPagingRequest {
}
export interface NotificationRequest extends GLRequest, NotificationModel {
}
export interface NotificationResponse extends GLResponse, NotificationModel {
}

export enum NotificationRoles {
    All = -1,
    Teacher = 0,
    Parent = 1,
    CampusAdmin = 2,
    SchoolAdmin = 3,
    RegionAdmin = 4,
    TrainingAdmin = 5,
    Trainer = 6,
    GlobalHead = 7,
    AccountManager = 9,
    TrainingManager = 10,
    IndividualUser = 89
}
export enum NotificationRoleNames {
    All = 'All',
    Teacher = 'Teacher',
    Parent = 'Parent',
    CampusAdmin = 'Campus Administrator',
    SchoolAdmin = 'School Administrator',
    RegionAdmin = 'Region Administrator',
    TrainingAdmin = 'Training Administrator',
    Trainer = 'Coach',
    GlobalHead = 'GlobalHead',
    AccountManager = 'Account Manager',
    IndividualUser = 'Individual User',
    TrainingManager = 'Training Manager',
}
export enum NotificationTargetTypes {
    All = 0,
    Region = 1,
    School = 2,
    Campus = 3,
    SchoolClass = 4,
    IndividualUser = 5,
}
export enum NotificationTargetTypeNames {
    All = 'All',
    Region = 'Region',
    School = 'School',
    Campus = 'Campus',
    SchoolClass = 'School Class',
    IndividualUser = 'Individual User',
}
export enum NotificationStatus {
    Submitted = 0,
    Approved = 1,
    Rejected = 2
}

// BE rejects a create/update payload carrying `scopeType: 0` — `scopeType` must be `null`
// or a valid non-zero enum value. UI's Target Type = `All` (0) only maps to backend
// `scopeType: null` (see service.ts's `scopeToTarget()`, which reads a `null` `scopeType`
// back as UI Target Type `All`) for these two roles, whose "every school/campus/class"
// broadcast is a scope BE actually recognizes. `NotificationRoles.All` is deliberately NOT
// on this list — `Role = All` + Target Type = `All` has no valid BE scope either. Every
// other role combined with Target Type `All` must be blocked before a create/update ever
// reaches the network. Lives here (a plain data-model file with no React/DI/service
// dependencies) so both the page-level submit validation (`notificationfill.logic.ts`) and
// the service's own write-payload mapping (`service.ts`) import the SAME source of truth —
// `service.ts` must never import page logic, and the whitelist must never be duplicated.
export const ALL_TARGET_TYPE_SUPPORTED_ROLES: NotificationRoles[] = [
    NotificationRoles.TrainingAdmin,
    NotificationRoles.GlobalHead,
];

export function isAllTargetTypeSupportedForRole(role: NotificationRoles): boolean {
    return ALL_TARGET_TYPE_SUPPORTED_ROLES.indexOf(role) !== -1;
}
